// StudioSources — the right zone's Sources tab (pen "Sources tab"): every
// source on the project, its file type and how far it has been read; a
// click opens it in the middle. Add a source uploads (the app's one file
// door). What each source gave is not projected yet, so it is not shown.
// Pure.

export type { StudioSource };
export { StudioSources };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import type { SourceReadStatus as ReadStatus } from "../../app/sourceReadStatus.js";
import { Button } from "../Button/Button.js";
import { EmptyState } from "../EmptyState/EmptyState.js";
import { Icon } from "../Icon/Icon.js";
import { SourceReadStatus } from "../SourceReadStatus/SourceReadStatus.js";

type StudioSource = {
    readonly id: string;
    readonly title: string;
    // The file's format tag ("PDF"), "" when none.
    readonly format: string;
    readonly readStatus: ReadStatus | undefined;
};

const StudioSources = ({
    sources,
    online,
    onOpen,
    onAdd,
}: {
    readonly sources: readonly StudioSource[];
    readonly online: boolean;
    readonly onOpen: (id: string) => void;
    readonly onAdd: () => void;
}): ReactNode => {
    const { t } = useLingui();
    const add = (
        <Button
            variant="secondary"
            size="sm"
            disabled={!online}
            onClick={onAdd}
        >
            <Icon name="plus" size="sm" />
            {t`Add a source`}
        </Button>
    );
    if (sources.length === 0)
        return (
            <EmptyState
                title={t`No sources yet`}
                body={t`Add a file or a link, and what you make can be written from it.`}
            >
                {add}
            </EmptyState>
        );
    return (
        <div className="bilda-studio-sources">
            <ul className="bilda-studio-sources__rows">
                {sources.map((s) => (
                    <li key={s.id}>
                        <button
                            type="button"
                            className="bilda-studio-sources__row"
                            onClick={() => onOpen(s.id)}
                        >
                            <Icon name="source" size="sm" />
                            <span
                                className="bilda-studio-sources__title"
                                title={s.title}
                            >
                                {s.title}
                            </span>
                            {s.format === "" ? null : (
                                <span className="bilda-studio-sources__format">
                                    {s.format}
                                </span>
                            )}
                            <SourceReadStatus status={s.readStatus} />
                        </button>
                    </li>
                ))}
            </ul>
            <div className="bilda-studio-sources__foot">{add}</div>
        </div>
    );
};
